import React from 'react';
import { motion } from 'framer-motion';
import { Link2, ShieldCheck, Database, Globe2, RefreshCw } from 'lucide-react';

interface ProcessNode {
  icon: React.FC<{ className?: string }>;
  title: string;
  description: string;
  color: string;
  tag: string;
}

const PROCESS_NODES: ProcessNode[] = [
  {
    icon: Link2,
    title: 'Connect',
    description: 'Link wallets, Discord, GitHub, and X. amPortal ingests on-chain and off-chain signals tied to your identity.',
    color: '#3BFF7A',
    tag: '01'
  },
  {
    icon: ShieldCheck,
    title: 'Verify',
    description: 'AO validates contributions with cryptographic checks. Reputation is verified, not self-reported.',
    color: '#0EF37F',
    tag: '02'
  },
  {
    icon: Database,
    title: 'Archive',
    description: 'Arweave stores identity signals permanently. Your reputation outlives platforms and accounts.',
    color: '#8BFFAD',
    tag: '03'
  },
  {
    icon: Globe2,
    title: 'Publish',
    description: 'ArNS issues your amprofile undername so your passport is readable and portable anywhere.',
    color: '#6BFFB0',
    tag: '04'
  },
  {
    icon: RefreshCw,
    title: 'Auto‑sync',
    description: 'amProfile refreshes on a cadence so new activity is reflected without manual updates.',
    color: '#57FFC0',
    tag: '05'
  }
];

export const HowItWorks: React.FC = () => {
  return (
    <section id="how-it-works" className="py-32 px-6 relative z-10">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[420px] bg-green-500/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full bg-white/[0.03] border border-green-500/20 backdrop-blur-md shadow-[0_0_15px_rgba(0,255,157,0.05)]">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-medium text-green-300">True flow</span>
            </div>

            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-white/60"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              How amProfile
              <br className="hidden md:block" /> actually works
            </h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-lg md:text-xl text-white/60 max-w-md font-light leading-relaxed"
          >
            Identity signals are verified on AO, stored on Arweave, published via ArNS, and kept fresh automatically.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-6">
          {PROCESS_NODES.map((node, index) => {
            const Icon = node.icon;
            return (
              <motion.div
                key={node.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: index * 0.1, type: "spring", bounce: 0.2 }}
                className="group relative bg-black/20 border border-white/10 rounded-[2rem] p-6 lg:p-7 backdrop-blur-md hover:border-green-400/40 transition-all duration-500 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 via-transparent to-green-500/0 group-hover:from-green-500/[0.03] group-hover:to-transparent transition-colors duration-500" />

                <div
                  className="absolute -bottom-6 -right-2 text-[110px] font-black leading-none text-white/[0.02] group-hover:text-green-500/[0.04] transition-colors duration-500 pointer-events-none select-none"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {node.tag}
                </div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-8">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_25px_rgba(0,255,157,0.2)]"
                      style={{
                        background: `${node.color}15`,
                        color: node.color,
                        border: `1px solid ${node.color}30`
                      }}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-white/20 group-hover:text-green-400/50 transition-colors duration-300">
                      Step {node.tag}
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-white mb-3 group-hover:text-green-300 transition-colors duration-300">
                    {node.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed font-light text-sm">
                    {node.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pipeline strip */}
        <div className="mt-16">
          <div className="relative rounded-3xl border border-white/10 bg-black/30 backdrop-blur-xl p-6 overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100%_6px] pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00ff9d]/50 to-transparent" />

            <div className="grid md:grid-cols-5 gap-4 items-center">
              {PROCESS_NODES.map((node, index) => (
                <div key={`pipe-${node.tag}`} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{
                      background: `${node.color}20`,
                      border: `1px solid ${node.color}40`,
                      color: node.color
                    }}
                  >
                    <node.icon className="w-4 h-4" />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                    {node.title}
                  </div>
                  {index < PROCESS_NODES.length - 1 && (
                    <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-white/10 via-[#00ff9d]/30 to-white/10" />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 text-xs uppercase tracking-[0.2em] text-white/40">
              Verified → Archived → Published → Auto‑synced
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
